<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap :color="unreadCount > 0 ? 'danger' : 'secondary'">
          <template #text>
            {{ unreadCount }}
          </template>
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[460px] sm:h-auto sm:min-w-[300px]">
      <section v-if="notificationsWithRelativeTime.length > 0" class="sm:max-h-[320px] p-4 overflow-auto">
        <VaList class="space-y-1 mb-2">
          <template v-for="item in notificationsWithRelativeTime" :key="item.id">
            <div
              class="notification-item"
              :class="{
                'notification-unread': !item.markAsRead,
                'notification-read': item.markAsRead,
              }"
            >
              <!-- 1st row: icon, title, updateTimestamp -->
              <div class="notification-row flex items-center justify-between mb-1">
                <div class="flex items-center gap-2">
                  <VaIcon :name="item.icon" :color="item.iconColor" />
                  <span v-if="item.title" class="font-bold">{{ item.title }}</span>
                </div>
                <span class="text-xs text-gray-500">{{ item.updateTimestamp }}</span>
              </div>
              <!-- 2nd row: notice with read more -->
              <div v-if="item.notice" class="notification-row text-gray-700 mb-2">
                <span v-if="!expandedNotices[item.id]">
                  {{ getTruncatedNotice(item.notice) }}
                  <template v-if="shouldShowReadMore(item.notice)">
                    <a href="#" class="text-primary ml-2" @click.prevent="expandedNotices[item.id] = true">Read more</a>
                  </template>
                </span>
                <span v-else>
                  {{ item.notice }}
                  <a href="#" class="text-primary ml-2" @click.prevent="expandedNotices[item.id] = false">Show less</a>
                </span>
              </div>
              <!-- 3rd row: actions -->
              <div class="notification-row notification-actions flex gap-2">
                <VaButton
                  v-if="!item.markAsRead"
                  preset="secondary"
                  size="small"
                  color="info"
                  @click="markAsRead(item.id)"
                >
                  Mark as Read
                </VaButton>
                <VaButton preset="secondary" size="small" color="danger" @click="dismissNotification(item.id)">
                  Dismiss
                </VaButton>
              </div>
            </div>
          </template>
        </VaList>

        <VaButton preset="primary" class="w-full" @click="displayAllNotifications = !displayAllNotifications">
          {{ displayAllNotifications ? 'See less' : 'See all' }}
        </VaButton>
      </section>
      <!-- no notifications message -->
      <section v-else class="p-4 text-center text-gray-500">
        <p>No notifications</p>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'vuestic-ui'
import api from '../../../../services/api'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

const { init } = useToast()

const baseNumberOfVisibleNotifications = 4
const displayAllNotifications = ref(false)

interface INotification {
  id: number
  title: string
  notice: string
  icon: string
  iconColor: string
  updateTimestamp: Date
  notice_type: string
  markAsRead: boolean
}

interface INoticeable {
  read_at: string | null
}

interface IApiResponseItem {
  id: number
  title: string
  notice: string
  notice_type: string
  published_at: string
  created_at: string
  noticeables?: INoticeable[]
}

const noticeTypeToColor: Record<string, string> = {
  notice: 'primary',
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'danger',
}

const notifications = ref<INotification[]>([])

const fetchNotifications = async () => {
  try {
    const { data } = await api.getMyNotice()
    notifications.value = (data?.data || []).map((item: IApiResponseItem) => {
      const type = (item.notice_type || 'notice').toLowerCase()
      return {
        id: item.id,
        title: item.title || '',
        notice: item.notice || '',
        icon: 'notifications',
        iconColor: noticeTypeToColor[type] || 'primary',
        markAsRead: item.noticeables?.[0]?.read_at ? true : false,
        updateTimestamp: new Date(item.published_at || item.created_at),
        notice_type: item.notice_type || 'notice',
      }
    })
  } catch (e) {
    notifications.value = []
  }
}

const markAsRead = async (id: number) => {
  try {
    await api.markAsRead(id)
    fetchNotifications()
  } catch (error) {
    init({
      message: (error as any)?.response?.data?.message || 'Failed to mark as read.',
      color: 'danger',
    })
  }
}

const dismissNotification = async (id: number) => {
  try {
    await api.noticeDismiss(id)
    fetchNotifications()
  } catch (error) {
    init({
      message: (error as any)?.response?.data?.message || 'Failed to dismiss notification.',
      color: 'danger',
    })
  }
}

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

// Use English relative time format directly
const rtf = new Intl.RelativeTimeFormat('en', { style: 'short' })

const notificationsWithRelativeTime = computed(() => {
  const list = displayAllNotifications.value
    ? notifications.value
    : notifications.value.slice(0, baseNumberOfVisibleNotifications)

  return list.map((item, index) => {
    const timeDifference = Math.round(new Date().getTime() - new Date(item.updateTimestamp).getTime())
    const timeName = getTimeName(timeDifference)

    let separator = false

    const nextItem = list[index + 1]
    if (nextItem) {
      const nextItemDifference = Math.round(new Date().getTime() - new Date(nextItem.updateTimestamp).getTime())
      const nextItemTimeName = getTimeName(nextItemDifference)

      if (timeName !== nextItemTimeName) {
        separator = true
      }
    }

    return {
      ...item,
      title: item.title,
      notice: item.notice,
      iconColor: item.iconColor,
      notice_type: item.notice_type,
      updateTimestamp: rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})

const unreadCount = computed(() => notifications.value.filter((n) => !n.markAsRead).length)

const expandedNotices = ref<Record<number, boolean>>({})

// Helper to check if notice needs "Read more"
function shouldShowReadMore(notice: string) {
  const wordLimit = 15
  const lineLimit = 2
  const words = notice.trim().split(/\s+/)
  if (words.length > wordLimit) return true
  // Estimate lines by character count (assuming ~50 chars per line)
  return notice.length > lineLimit * 50
}

function getTruncatedNotice(notice: string) {
  const wordLimit = 15
  const words = notice.trim().split(/\s+/)
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...'
  }
  // Truncate by character for 2 lines (~100 chars)
  if (notice.length > 100) {
    return notice.slice(0, 100) + '...'
  }
  return notice
}

onMounted(() => {
  fetchNotifications()
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: flex-start;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.notification-item {
  background: var(--va-background-secondary, #f8f9fa);
  border: 1px solid var(--va-background-border, #e5e7eb);
  border-radius: 10px;
  margin-bottom: 12px;
  padding: 14px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition:
    box-shadow 0.2s,
    background 0.2s;
  align-items: flex-start;

  .notification-row {
    width: 100%;
  }
  .font-bold {
    font-size: 1.05em;
  }
  .notification-actions {
    margin-top: 2px;
    display: flex;
    gap: 8px;
  }
}

.notification-unread {
  background: #eaf6ff;
  border-left: 4px solid #2196f3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.07);
}

.notification-read {
  background: #f8f9fa;
  opacity: 0.7;
}
</style>
